import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CreditCardIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import adminAPI from "../utils/api";
import toast from "react-hot-toast";
import ServiceModal from "../components/ServiceModal";
import ConfirmationModal from "../components/ConfirmationModal";

const STATUS_OPTIONS = [
  { value: "all", label: "Tous les statuts" },
  { value: "active", label: "Actifs" },
  { value: "inactive", label: "Inactifs" },
];

const Services = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(() => {
    const s = searchParams.get("status") || "all";
    return ["all", "active", "inactive"].includes(s) ? s : "all";
  });
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create', 'edit', 'view'
  const [selectedService, setSelectedService] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => parseInt(searchParams.get("page"), 10) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Sync URL with filters and page
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.set("search", searchTerm.trim());
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (currentPage > 1) params.set("page", String(currentPage));
    setSearchParams(params, { replace: true });
  }, [searchTerm, statusFilter, currentPage, setSearchParams]);

  // Debounced search term for API calls
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const prevSearchRef = useRef(debouncedSearch);

  // Fetch services from API (server-side pagination + status)
  const fetchServices = async (page, pageSize, search, status) => {
    try {
      setLoading(true);
      const isActive =
        status === "active" ? true : status === "inactive" ? false : undefined;
      const data = await adminAPI.getServiceTypes({
        page,
        page_size: pageSize,
        ...(search ? { search } : {}),
        ...(isActive !== undefined ? { is_active: isActive } : {}),
      });
      const list = Array.isArray(data) ? data : (data?.results ?? []);
      setServices(list);
      setTotalCount(typeof data?.count === "number" ? data.count : list.length);
    } catch (err) {
      console.error("Error fetching services:", err);
      setError(err.message);
      toast.error("Erreur lors du chargement des services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const searchChanged = prevSearchRef.current !== debouncedSearch;
    if (searchChanged) {
      prevSearchRef.current = debouncedSearch;
      setCurrentPage(1);
    }
    const pageToFetch = searchChanged ? 1 : currentPage;
    fetchServices(pageToFetch, itemsPerPage, debouncedSearch, statusFilter);
  }, [currentPage, itemsPerPage, debouncedSearch, statusFilter]);

  // Handle delete service
  const handleDeleteService = (service) => {
    setServiceToDelete(service);
    setShowDeleteModal(true);
  };

  // Confirm delete service
  const confirmDeleteService = async () => {
    if (!serviceToDelete) return;

    try {
      setDeleteLoading(true);
      await adminAPI.deleteServiceType(serviceToDelete.id);
      toast.success("Service supprimé avec succès");
      fetchServices(currentPage, itemsPerPage, debouncedSearch, statusFilter); // Refresh the list
      setShowDeleteModal(false);
      setServiceToDelete(null);
    } catch (err) {
      console.error("Error deleting service:", err);
      toast.error("Erreur lors de la suppression du service");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle view service
  const handleViewService = (service) => {
    setSelectedService(service);
    setModalMode("view");
    setShowModal(true);
  };

  // Handle edit service
  const handleEditService = (service) => {
    setSelectedService(service);
    setModalMode("edit");
    setShowModal(true);
  };

  // Handle create service
  const handleCreateService = () => {
    setSelectedService(null);
    setModalMode("create");
    setShowModal(true);
  };

  // Server-side pagination: total from API
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const startIndex = totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalCount);



  return (
    <div className="">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <CreditCardIcon className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Gestion des Services
            </h1>
            <p className="text-gray-600">Gérez les packs et abonnements</p>
          </div>
        </div>
        <button
          onClick={handleCreateService}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Nouveau Service</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Rechercher un service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="statusFilter" className="text-sm text-gray-600 whitespace-nowrap">
              Statut:
            </label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500 min-w-[140px]"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Services Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sessions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clients
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {
                loading ?
                  (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    </div>
                  )
                  : error ?
                    (
                      <div className="text-center py-12">
                        <div className="text-red-600 mb-4">Erreur: {error}</div>
                        <button
                          onClick={() => window.location.reload()}
                          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
                        >
                          Réessayer
                        </button>
                      </div>
                    )
                    : services.length === 0 ? (
                      <tr>
                        <td
                          colSpan="7"
                          className="px-6 py-12 text-center text-gray-500"
                        >
                          <CreditCardIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                          <p className="text-lg font-medium">Aucun service trouvé</p>
                          <p className="text-sm">
                            Commencez par ajouter un nouveau service
                          </p>
                        </td>
                      </tr>
                    ) : (
                      services.map((service) => (
                        <tr key={service.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {service.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {service.description.length > 15
                                  ? service.description.slice(0, 15) + "..."
                                  : service.description}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${service.type === "pack"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-purple-100 text-purple-800"
                                }`}
                            >
                              {service.type === "pack" ? "Pack" : "Abonnement"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {service.session_count}
                            {service.type === "abonnement" && " / mois"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {service.price ? `${service.price} MAD` : "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              <UsersIcon className="h-4 w-4 mr-1 text-gray-400" />
                              {service.client_count || 0}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${service.is_active
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                                }`}
                            >
                              {service.is_active ? "Actif" : "Inactif"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleViewService(service)}
                                className="text-gray-600 hover:text-gray-900 p-1 rounded"
                                title="Voir les détails"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEditService(service)}
                                className="text-primary-600 hover:text-primary-900 p-1 rounded"
                                title="Modifier"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteService(service)}
                                className="text-red-600 hover:text-red-900 p-1 rounded"
                                title="Supprimer"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-600">
              Affichage {startIndex}–{endIndex} sur {totalCount} service(s)
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                aria-label="Page précédente"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => {
                    if (totalPages <= 7) return true;
                    if (p === 1 || p === totalPages) return true;
                    if (Math.abs(p - currentPage) <= 1) return true;
                    return false;
                  })
                  .map((p, i, arr) => (
                    <React.Fragment key={p}>
                      {i > 0 && arr[i - 1] !== p - 1 && (
                        <span className="px-2 text-gray-400">…</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(p)}
                        className={`min-w-[2.25rem] py-2 px-2 rounded-lg text-sm font-medium ${currentPage === p
                            ? "bg-primary-600 text-white"
                            : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
              </div>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                aria-label="Page suivante"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Service Modal */}
      <ServiceModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        service={selectedService}
        mode={modalMode}
        onSuccess={() => fetchServices(currentPage, itemsPerPage, debouncedSearch, statusFilter)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteService}
        title="Supprimer le service"
        message={`Êtes-vous sûr de vouloir supprimer le service "${serviceToDelete?.name}" ? Cette action est irréversible et affectera tous les clients qui utilisent ce service.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default Services;
